import { AbstractControl, FormControl, FormGroup, UntypedFormControl, ValidatorFn } from '@angular/forms';
import { electronicFormatIBAN } from 'ibantools';
import moment, { Moment } from 'moment';
import { AvailableBusinessVolumeStatusByType, BusinessVolumeCostStateEnum, BusinessVolumeStatusEnum } from '../dave-data-module/entities/business-volume.entity';
import {
    arlBaseCostAllowanceFormGroup,
    arlBaseCostAllowanceType,
} from '../dave-reports/components/arl-calculation/arl-form-data.service';
import { SelectSearchOption } from '../dave-utils-module/select-search/components/select-search/select-search.component';
import { FormGroupTyped } from '../dave-utils-module/typings';
import { isNotNullOrUndefined } from './helper';
import { USER_TO_EVENT_TIMESPAN_REGEX } from '../dave-data-module/entities/user-to-event.entity';

export const hasRequiredValidator = (abstractControl: AbstractControl): boolean => {
    if (abstractControl.validator) {
        const validator = abstractControl.validator({} as AbstractControl);
        return !!validator?.required;
    }
};
export function getErrorMessage(Value: UntypedFormControl | AbstractControl) {
    if (Value.hasError('required') || Value.hasError('requiredWhenValidator')) {
        return 'Bitte füllen Sie dieses Feld aus.';
    }
    if (Value.hasError('allowanceNotSmallerZero')) {
        return 'Rabatt muss größer 0 sein';
    }
    if (Value.hasError('surchargeNotTallerZero')) {
        return 'Zuschlag muss größer 0 sein';
    }
    if (Value.hasError('minlength')) {
        return 'Bitte geben Sie mindestens ' + Value.errors['minlength']['requiredLength'] + ' Zeichen ein.';
    }
    if (Value.hasError('maxlength')) {
        return 'Bitte geben Sie maximal ' + Value.errors['maxlength']['requiredLength'] + ' Zeichen ein.';
    }
    if (Value.hasError('email')) {
        return 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
    }
    if (Value.hasError('phoneNumberValidator')) {
        return 'Bitte geben Sie eine gültige Telefonnummer ein.';
    }
    if (Value.hasError('coordValidator')) {
        return 'Bitte verwenden Sie ausschließlich Ziffern von 0 - 9 und einen Dezimalpunkt.';
    }
    if (Value.hasError('matDatepickerMax')) {
        return `Das Datum muss vor ${(Value.errors.matDatepickerMax.max as Moment).format('D.M.YYYY')} liegen`;
    }
    if (Value.hasError('matDatepickerMin')) {
        return `Das Datum muss nach ${(Value.errors.matDatepickerMin.min as Moment).format('D.M.YYYY')} liegen`;
    }
    if (Value.hasError('decimalValidator')) {
        return `Es sind ${Value.errors.decimalValidator.allowedDecimals ? Value.errors.decimalValidator.allowedDecimals + '' : 'keine'} Ziffern nach dem Komma erlaubt`;
    }
    if (Value.hasError('min')) {
        return 'Der eingegebene Wert ist zu niedrig';
    }
    if (Value.hasError('licenseAlreadyBookedValidator')) {
        return 'Diese Lizenz besitzen Sie bereits.';
    }
    if (Value.hasError('passwordValidator')) {
        return 'Ein Passwort muss aus mindestens 8 zeichen und mindestens einer Zahl, einem großen und einem kleinen Buchstaben bestehen.';
    }
    if (Value.hasError('equalPasswordValidator')) {
        return 'Die Passwörter stimmen nicht überein.';
    }
    if (Value.hasError('iBANValidator')) {
        return 'Die eingegebene Zeichenfolge ist keine gültige IBAN.';
    }
    if (Value.hasError('bICValidator')) {
        return 'Die eingegebene Zeichenfolge ist keine gültige BIC.';
    }
    if (Value.hasError('numberValidator')) {
        return 'Bitte verwenden Sie nur Ziffern';
    }
    if (Value.hasError('decimalPriceValidator')) {
        return 'maximal 2 Stellen nach dem Komma';
    }
    if (Value.hasError('arrayLengthValidatorToLong')) {
        return 'Bitte weniger auswählen';
    }
    if (Value.hasError('arrayLengthValidatorToShort')) {
        return 'Bitte mehr auswählen';
    }
    if (Value.hasError('businessVolumeStateNotAvailable')) {
        return 'Bitte Wählen Sie einen anderen Status.';
    }
    if (Value.hasError('duplicate')) {
        return 'Bereits vorhanden.';
    }
    if (Value.hasError('combinedMaxLength')) {
        const { max, fieldNames, lengthdescription } = Value.errors['combinedMaxLength'];
        const names = fieldNames?.length === 2 ? fieldNames.join(' & ') : 'Diese Felder';
        return names + ' dürfen zusammen maximal ' +max + lengthdescription + 'Zeichen enthalten.';
    }
    if (Value.hasError('sharepointFolderNameValidator')) {
        return 'zulässige Zeichen: a-z A-Z 0-9 äöüÄÖÜ ß & _ - ( )';
    }
    if (Value.hasError('userToEventTimespanValidator')) {
        return 'geben sie eine Zahl ein';
    }
    return 'Bitte überprüfen Sie Ihre Eingabe.';
}

export function iBANValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const ibantools = require('ibantools');
        if (control.value == null || control.value.length === 0) {
            return null;
        }
        const iban = electronicFormatIBAN(control.value);
        const validIBAN = ibantools.isValidIBAN(iban);
        return validIBAN ? null : { iBANValidator: { value: control.value } };
    };
}

export function bICValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const ibantools = require('ibantools');
        if (control.value == null || control.value.length === 0) {
            return null;
        }
        const validBIC = ibantools.isValidBIC(control.value);
        return validBIC ? null : { bICValidator: { value: control.value } };
    };
}
export function numberValidator(nameRe: RegExp = /^[0-9]*$/): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const forbidden = !(nameRe[Symbol.match](control.value) || control.value === null);
        return forbidden ? { numberValidator: { value: control.value } } : null;
    };
}

export function decimalValidator(decimalCount: number = 0): ValidatorFn {
    const reg = new RegExp(`^-?\\d*[.,]?\\d{0,${decimalCount}}$`, 'g');
    return (control: AbstractControl): { [key: string]: any } | null => {
        const forbidden = !(reg[Symbol.match](control.value) || control.value === null);
        return forbidden ? { decimalValidator: { value: control.value, allowedDecimals: decimalCount } } : null;
    };
}
export function decimalPriceValidator(): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        return decimalValidator(2)(control) ? { decimalPriceValidator: { value: control.value } } : null;
    };
}

export function phoneNumberValidator(
    // numberRe: RegExp = /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/g
    numberRe: RegExp = /(.*)/g,
): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) {
            return null; // leere Werte sind okay, für nichtleere Werte gibts required
        }
        const allowed = numberRe[Symbol.match](control.value);
        return allowed ? null : { phoneNumberValidator: { value: control.value } };
    };
}

export function coordValidator(nameRe: RegExp = /[^0-9 .]/g): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const forbidden = nameRe[Symbol.match](control.value);
        return forbidden ? { coordValidator: { value: control.value } } : null;
    };
}

export function passwordValidator(): ValidatorFn {
    const hasNumberRe: RegExp = /\d/;
    const hasCapitalCaseRe: RegExp = /[A-Z]/;
    const hasLowerCaseRe: RegExp = /[a-z]/;

    return (control: AbstractControl): { [key: string]: any } | null => {
        const forbidden = control.value.length < 8 || !hasNumberRe[Symbol.match](control.value) || !hasCapitalCaseRe[Symbol.match](control.value) || !hasLowerCaseRe[Symbol.match](control.value);
        return forbidden ? { passwordValidator: { value: control.value } } : null;
    };
}

/**
 * Angular-Validierungsfunktion für eine FormGroup mit den Feldern `Firstname`,
 * `Lastname`, `Email` und `PhoneNumber`.
 *
 * Überprüft, ob mindestens ein Feld bei der Erstellung oder Bearbeitung eines
 * Ansprechpartners ausgefüllt wurde.
 *
 * Gibt entweder `null` oder ein Fehlerobjekt mit dem Wert
 * `contactPersonRequiredFieldValidator` zurück.
 */
export function contactPersonRequiredFieldValidator(
    formGroup: FormGroupTyped<{
        Firstname: string;
        Lastname: string;
        Email: string;
        PhoneNumber: string;
    }>,
): { [key: string]: any } | null {
    const { Firstname, Lastname, Email, PhoneNumber } = formGroup.value;

    return Firstname || Lastname || Email || PhoneNumber ? null : { contactPersonRequiredFieldValidator: true };
}

export function userRequiredFieldValidator(
    formGroup: FormGroupTyped<{
        Firstname: string;
        Lastname: string;
        Email: string;
    }>,
): { [key: string]: any } | null {
    const { Firstname, Lastname, Email } = formGroup.value;
    return Firstname || Lastname || Email ? null : { userRequiredFieldValidator: true };
}

export function equalPasswordValidator(Password: () => FormControl<string>) {
    return (RepeatedPassword: FormControl<string>) => (`${Password()?.value}` === `${RepeatedPassword.value}` ? null : { equalPasswordValidator: true });
}
export function requiredWhenValidator(condition: () => boolean) {
    return (form: FormControl<any>) => (!condition() || isNotNullOrUndefined(form.value) ? null : { requiredWhenValidator: true });
}

/**
 * Angular-Validierungsfunktion für eine FormGroup mit den Feldern
 * `EventDate`, `EventEndDate`,`EventStartTimeString` und `EventEndTimeString`.
 *
 * Überprüft, ob die StartTime vor der EndTime liegt.
 *
 * Gibt entweder `null` oder ein Fehlerobjekt mit dem Wert
 * `startTimeBeforeEndTimeValidator` zurück.
 */
export function startTimeBeforeEndTimeValidator(
    formGroup: FormGroupTyped<{
        EventDate: Date | Moment;
        EventEndDate: Date | Moment;
        EventStartTimeString: string;
        EventEndTimeString: string;
    }>,
): { [key: string]: any } {
    const { EventEndDate, EventDate, EventStartTimeString, EventEndTimeString } = formGroup.value;
    if (!EventStartTimeString || !EventEndTimeString || !EventEndDate || !EventDate) {
        return null;
    }

    const min = EventStartTimeString.split(':');
    const max = EventEndTimeString.split(':');

    const eventDate = moment(EventDate).toDate();
    const eventEndDate = moment(EventEndDate).toDate();

    eventDate.setHours(0, 0, 0, 0);
    eventEndDate.setHours(0, 0, 0, 0);

    if (eventEndDate > eventDate) {
        return null;
    } else if (eventDate > eventEndDate) {
        return { startTimeBeforeEndTimeValidator: true };
    }
    return min[0] > max[0] // Stunden falsch
        ? { startTimeBeforeEndTimeValidator: true }
        : min[0] === max[0] && min[1] > max[1] // Stunden gleich, Minuten falsch
        ? { startTimeBeforeEndTimeValidator: true }
        : null;
}
export function licenseAlreadyBookedValidator(minNumber: number = 0, maxNumber: number = 0): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        if (!control.value) {
            return null; // leere Werte sind okay, für nichtleere Werte gibts required
        }
        // Wert darf nicht zwischen minNumber und maxNumber sein, maxNumber = 0 ist ein Spezialfall,
        // der bedeutet, das maxNumber eigentlich unbegrenzt ist
        const forbidden = minNumber <= control.value && (maxNumber >= control.value || maxNumber === 0);
        return forbidden ? { licenseAlreadyBookedValidator: true } : null;
    };
}

export function arrayLengthValidator(minLength: number = null, maxLength: number = null): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        // Wert darf nicht zwischen minNumber und maxNumber sein, maxNumber = 0 ist ein Spezialfall,
        // der bedeutet, das maxNumber eigentlich unbegrenzt ist
        const toShort = minLength !== null && (control.value?.length < minLength || (!control.value && minLength > 0));
        const toLong = maxLength !== null && control.value?.length > maxLength;
        return toShort ? { arrayLengthValidatorToShort: true } : toLong ? { arrayLengthValidatorToLong: true } : null;
    };
}
export function bvStatusByBvTypedValidator(getType: () => BusinessVolumeCostStateEnum) {
    return (statusFormControl: FormControl<SelectSearchOption<{ optionValue: BusinessVolumeStatusEnum }>>) => {
        const type = getType();
        return type && !AvailableBusinessVolumeStatusByType.get(type).includes(statusFormControl?.value?.optionValue) ? { businessVolumeStateNotAvailable: true } : null;
    };
}
export function someRequiredValidator<T = { [key: string]: FormControl }>(requiredControlNames: string[], errorCode = 'someRequired') {
    return (form: FormGroup<any>) => {
        return requiredControlNames.some((control) => form.get(control).value) ? null : { [errorCode]: true };
    };
}
export function duplicationValidator<T>(duplicates: Array<T>): ValidatorFn {
    return (control: FormControl<T>): { [key: string]: any } | null => {
        return control.value && duplicates.includes(control.value) ? { duplicate: true } : null;
    };
}
export function aRLBaseCostAllowanceValidator(formGroup: arlBaseCostAllowanceFormGroup): { [key: string]: any } {
    const { percent, type } = formGroup.value;
    if (isNotNullOrUndefined(percent) && ((percent > 0 && type === 'surcharge') || (percent < 0 && type === 'allowance'))) {
        return null;
    } else if (type === 'allowance') {
        return { allowanceNotSmallerZero: true };
    }
    return { surchargeNotTallerZero: true };
}

export function aRLBaseCostAllowancePercentValidator(type: arlBaseCostAllowanceType): ValidatorFn {
    return (formControl: FormControl<number>) => {
        const percent = formControl.value;
        if (isNotNullOrUndefined(percent) && (type === 'surchargeOverride' || (percent > 0 && type === 'surcharge') || (percent < 0 && type === 'allowance'))) {
            return null;
        } else if (type === 'allowance') {
            return { allowanceNotSmallerZero: true };
        }
        return { surchargeNotTallerZero: true };
    }
}

export function sharepointFolderNameValidator(nameRe: RegExp = /^[A-Za-z0-9äöüÄÖÜß &_()\-]*$/): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
        const forbidden = !(nameRe[Symbol.match](control.value) || control.value === null);
        return forbidden ? { sharepointFolderNameValidator: { value: control.value } } : null;
    };
}


export function userToEventTimespanValidator(): ValidatorFn {
    const regex = USER_TO_EVENT_TIMESPAN_REGEX;
    return (control: AbstractControl): { [key: string]: any } | null => {
        const isValid = regex.test(control.value) || !control.value;
        return !isValid ? { userToEventTimespanValidator: { value: control.value } } : null;
    };
}
